import React, { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Menu,
  MenuItem,
  ListItemText,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
  AttachFile as AttachIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Fullscreen as MaximizeIcon,
  FullscreenExit as MinimizeIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { Icon } from '@iconify/react';
import { Call, Unit, PCRData } from '../types/index.ts';
import { useToast } from '../contexts/ToastContext.tsx';
import { useConfig } from '../contexts/ConfigContext.tsx';
import { useSocket } from '../contexts/SocketContext.tsx';
import { http } from '../lib/http';
import EditCallModal from './EditCallModal.tsx';
import PCRModal from './PCRModal.tsx';

/** ───────────────────────────────────────────────────────────
 *  Optimized CallDetails
 *  - Minimizes re-renders with memoized subcomponents + callbacks
 *  - Avoids querying DOM; uses anchors from click targets
 *  - Precomputes derived strings (toUpperCase, times) via useMemo
 *  - Uses refs for previousCall snapshot to reduce state churn
 *  - Tightened styles and conditionals while preserving visuals
 *  - Keeps API/behavior compatible with original component
 *  ─────────────────────────────────────────────────────────── */

// ---------- Static constants & helpers (stable references) ----------
const STATUS_COLORS: Record<string, string> = {
  'I/S': '#32be5a',
  'DISP': '#ffd25a',
  'E/R': '#f0b432',
  'O/S': '#3c96ff',
  'TRX': '#c896ff',
  'TRX ARR': '#b478f0',
  'OOS': '#a0a0a0',
  'OOS-GAME': '#787878'
};

const AVAILABLE_STATUSES = ['I/S', 'E/R', 'O/S', 'TRX', 'OOS'] as const;

const flashBackground = keyframes`
  0%, 100% { background-color: transparent; }
  50% { background-color: rgba(60, 140, 255, 0.25); }
`;
const flashTextColor = keyframes`
  0%, 100% { color: #ffffff; }
  50% { color: #3c8cff; }
`;
const flashBorder = keyframes`
  0%, 100% { border-color: #3c8cff; }
  50% { border-color: #ffffff; }
`;
const flashRowBorderColor = keyframes`
  0%, 100% { border-color: #3c8cff; }
  50% { border-color: #1e3a8a; }
`;

const jitterDuration = (baseMs = 500) => `${baseMs + (Date.now() % 7)}ms`;

const formatElapsed = (seconds: number) => {
  const sec = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h > 0
    ? `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    : `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const formatUnixAsMDHMS = (unixSec?: number) => {
  if (!unixSec) return '?';
  const d = new Date(unixSec * 1000);
  const mm = (d.getMonth() + 1).toString().padStart(2, '0');
  const dd = d.getDate().toString().padStart(2, '0');
  const hh = d.getHours().toString().padStart(2, '0');
  const mi = d.getMinutes().toString().padStart(2, '0');
  const ss = d.getSeconds().toString().padStart(2, '0');
  return `${mm}/${dd} ${hh}:${mi}:${ss}`;
};

interface CallDetailsProps {
  call: Call;
  units: Unit[];
  selectedUnits: string[];
  onAttachUnits: (unitIds: string[]) => void;
  onCommentsViewed?: (commentIds: string[]) => void;
  currentUserId?: string;
  currentUsername?: string;
  isMaximized?: boolean;
  onToggleMaximize?: () => void;
  onCallClosed?: () => void;
  showEditAndCloseButtons?: boolean;
  allowSelfDispatch?: boolean;
  allowSelfClose?: boolean;
}

// ---------- Small memoized subcomponents ----------
const AssignedUnitChip = memo(function AssignedUnitChip({
  unit,
  time,
  onOpenMenu
}: {
  unit: Unit;
  time: string;
  onOpenMenu: (anchorEl: HTMLElement, unitId: string) => void;
}) {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      onOpenMenu(e.currentTarget, unit.id);
    },
    [onOpenMenu, unit.id]
  );

  return (
    <Box
      key={unit.id}
      data-unit-id={unit.id}
      onClick={handleClick}
      sx={{
        backgroundColor: STATUS_COLORS[unit.status] ?? '#a0a0a0',
        border: '2px solid #40444b',
        borderRadius: '5px',
        width: '90px',
        height: '40px',
        position: 'relative',
        cursor: 'pointer',
        flexShrink: 0,
        '&:hover': { opacity: 0.9 }
      }}
    >
      <Typography
        sx={{
          color: '#0a0c0e',
          fontSize: 14,
          fontWeight: 'bold',
          lineHeight: 1,
          textAlign: 'left',
          position: 'absolute',
          left: '6px',
          top: '5px',
          width: 'calc(100% - 12px)',
          height: '14px'
        }}
      >
        {unit.id}
      </Typography>
      <Typography
        sx={{
          color: '#0a0c0e',
          fontSize: 11,
          lineHeight: 1,
          textAlign: 'left',
          position: 'absolute',
          left: '6px',
          top: '21px',
          width: 'calc(100% - 12px)',
          height: '12px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {time}
      </Typography>
    </Box>
  );
});

type PreparedComment = {
  id: string;
  number?: number;
  author: string;
  text: string;
  type?: string;
  timestamp: number;
  formattedTime: string;
  isFlashing: boolean;
  pcrData?: any;
};

const CommentsTable = memo(function CommentsTable({
  comments
}: {
  comments: PreparedComment[];
}) {
  return (
    <Box
      sx={{
        display: 'table',
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        border: '1px solid #40444b',
        borderRadius: '8px',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ display: 'table-header-group', backgroundColor: '#2a2d32' }}>
        <Box sx={{ display: 'table-row' }}>
          <Box sx={{ display: 'table-cell', p: '8px 12px', borderBottom: '1px solid #40444b', color: '#eceef1', fontSize: 14, fontWeight: 'bold', width: '40px' }}>#</Box>
          <Box sx={{ display: 'table-cell', p: '8px 12px', borderBottom: '1px solid #40444b', borderLeft: '1px solid #40444b', color: '#eceef1', fontSize: 14, fontWeight: 'bold', width: '20%' }}>AUTHOR</Box>
          <Box sx={{ display: 'table-cell', p: '8px 12px', borderBottom: '1px solid #40444b', borderLeft: '1px solid #40444b', color: '#eceef1', fontSize: 14, fontWeight: 'bold' }}>COMMENT</Box>
          <Box sx={{ display: 'table-cell', p: '8px 12px', borderBottom: '1px solid #40444b', borderLeft: '1px solid #40444b', color: '#eceef1', fontSize: 14, fontWeight: 'bold', width: '140px' }}>TIME</Box>
        </Box>
      </Box>

      <Box sx={{ display: 'table-row-group' }}>
        {comments.map((c, index) => {
          const baseBorder = index < comments.length - 1 ? '1px solid #40444b' : 'none';
          const isPCR = c.type === 'PCR';
          const clickable = isPCR && /^.*PCR\d+ generated by/.test(c.text);

          return (
            <Box
              key={c.id}
              sx={{
                display: 'table-row',
                backgroundColor: index % 2 === 0 ? '#1a1d22' : '#16181c',
                transition: 'background-color 0.3s ease',
                '&:hover': { backgroundColor: '#2a2d32' },
                ...(c.isFlashing ? {
                  position: 'relative',
                  // Draw a full-rectangle overlay that sits above the cells
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,                    // full row box
                    border: '2px solid #3c8cff', // visible on all four sides
                    pointerEvents: 'none',
                    zIndex: 1,
                    animation: `${flashRowBorderColor} 600ms ease-in-out 5`
                  }
                } : {})
              }}
              data-comment-id={c.id}
            >
              <Box sx={{ display: 'table-cell', p: '12px 16px', color: '#a0a6b1', fontSize: 13, fontWeight: 'bold', verticalAlign: 'top', borderBottom: baseBorder }}>
                {c.number ?? '?'}
              </Box>
              <Box
                sx={{
                  display: 'table-cell',
                  p: '12px 16px',
                  color: c.type === 'SYSTEM' ? '#a0a6b1' : '#ffffff',
                  fontSize: 13,
                  fontWeight: c.type === 'SYSTEM' ? 'normal' : 'bold',
                  fontStyle: c.type === 'SYSTEM' ? 'italic' : 'normal',
                  verticalAlign: 'top',
                  borderBottom: baseBorder,
                  borderLeft: '1px solid #40444b'
                }}
              >
                {c.author}
              </Box>
              <Box
                sx={{
                  display: 'table-cell',
                  p: '12px 16px',
                  color: c.type === 'SYSTEM' ? '#a0a6b1' : '#ffffff',
                  fontSize: 13,
                  lineHeight: 1.5,
                  fontStyle: c.type === 'SYSTEM' ? 'italic' : 'normal',
                  wordBreak: 'break-word',
                  verticalAlign: 'top',
                  borderBottom: baseBorder,
                  borderLeft: '1px solid #40444b',
                  cursor: clickable ? 'pointer' : 'default'
                }}
                onClick={() => {
                  if (!clickable) return;
                  const ev = new CustomEvent('open-pcr-from-comment', { detail: { id: c.id } });
                  window.dispatchEvent(ev);
                }}
              >
                {clickable ? (
                  <Typography sx={{ color: '#3c8cff', textDecoration: 'underline', fontSize: 13, lineHeight: 1.5, '&:hover': { color: '#5ba3ff' } }}>
                    {c.text}
                  </Typography>
                ) : (
                  c.text
                )}
              </Box>
              <Box sx={{ display: 'table-cell', p: '12px 16px', color: '#a0a6b1', fontSize: 13, whiteSpace: 'nowrap', verticalAlign: 'top', borderBottom: baseBorder, borderLeft: '1px solid #40444b' }}>
                {c.formattedTime}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
});

// ---------- Main component ----------
const CallDetails: React.FC<CallDetailsProps> = ({
  call,
  units,
  selectedUnits,
  onAttachUnits,
  onCommentsViewed,
  currentUserId,
  currentUsername,
  isMaximized = false,
  onToggleMaximize,
  onCallClosed,
  showEditAndCloseButtons = true,
  allowSelfDispatch = false,
  allowSelfClose = false
}) => {
  const { showSuccess, showError, showWarning } = useToast();
  const { callCodes } = useConfig();
  const socket = useSocket();

  // Local state kept minimal; many values are derived via useMemo
  const [newComment, setNewComment] = useState('');
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedUnitForStatus, setSelectedUnitForStatus] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const [flashingComments, setFlashingComments] = useState<Set<string>>(new Set());
  const [flashingFields, setFlashingFields] = useState<Set<string>>(new Set());
  const [isDragOver, setIsDragOver] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLastUnitPrompt, setShowLastUnitPrompt] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{ unitId: string; status: string } | null>(null);
  const [showPCRModal, setShowPCRModal] = useState(false);
  const [editingPCR, setEditingPCR] = useState<PCRData | null>(null);
  const [editingPCRCommentId, setEditingPCRCommentId] = useState<string | null>(null);

  // useRef for previous call data snapshot (doesn't trigger re-render when updated)
  const prevCallRef = useRef<Partial<Call> | null>(null);
  
  // Track comment IDs we've already seen to detect new ones
  const prevCommentIdsRef = useRef<Set<string>>(new Set());
  // Track active timeouts to prevent interference
  const timeoutRefsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Ticker
  useEffect(() => {
    const id = setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Reset "seen" comment IDs when the call id changes
  useEffect(() => {
    prevCommentIdsRef.current = new Set((call.comments || []).map(c => c.id));
    // Also clear any flashing remnants on call switch
    setFlashingComments(new Set());
    
    // Clear all active timeouts when call changes
    timeoutRefsRef.current.forEach(timeout => clearTimeout(timeout));
    timeoutRefsRef.current.clear();
  }, [call.id]);

  // Listen for real-time comment updates via Socket.IO
  useEffect(() => {
    if (!socket) return;

    const handleCommentAdded = (data: { callId: string; comment: any }) => {
      console.log('[CallDetails] INSTANT comment added via Socket.IO:', data);
      
      // Only handle comments for the current call
      if (data.callId === call.id) {
        console.log('[CallDetails] Comment added to current call, flashing new comment:', data.comment);
        
        // Flash the new comment to indicate it was just added
        setFlashingComments(prev => {
          const newSet = new Set(prev);
          newSet.add(data.comment.id);
          return newSet;
        });

        // Clear the flash after 3 seconds
        const timeout = setTimeout(() => {
          setFlashingComments(prev => {
            const newSet = new Set(prev);
            newSet.delete(data.comment.id);
            return newSet;
          });
        }, 3000);

        // Store timeout for cleanup
        timeoutRefsRef.current.set(data.comment.id, timeout);
      }
    };

    const handleCallUpdated = (updatedCall: any) => {
      console.log('[CallDetails] INSTANT call updated via Socket.IO:', updatedCall);
      
      // If this is the current call, the parent component should handle the update
      // This is just for logging and potential future enhancements
    };

    // Set up Socket.IO event listeners
    socket.on('comment-added', handleCommentAdded);
    socket.on('call-updated', handleCallUpdated);

    return () => {
      socket.off('comment-added', handleCommentAdded);
      socket.off('call-updated', handleCallUpdated);
    };
  }, [socket, call.id]);

  // Debug: Log when call prop changes to see if comments are being updated
  useEffect(() => {
    console.log('[CallDetails] Call prop updated:', {
      callId: call.id,
      commentCount: call.comments?.length || 0,
      comments: call.comments?.map(c => ({ id: c.id, text: c.text.substring(0, 50) + '...' })) || []
    });
  }, [call.comments, call.id]);

  // Derived: call codes helpers (depend only on callCodes)
  const getCallDescription = useCallback(
    (callCode: string) => {
    const codeData = callCodes[callCode];
      return (codeData ? codeData.desc : callCode).toUpperCase();
    },
    [callCodes]
  );

  const getCallBox = useCallback(
    (callCode: string) => {
    const codeData = callCodes[callCode];
    return codeData ? codeData.box : 'UNKNOWN PLAN';
    },
    [callCodes]
  );

  // Derived: elapsed since received
  const headerElapsed = useMemo(() => {
    if (!call.timeRcvdUnix) return '00:00';
    const elapsedTime = Math.floor(currentTime / 1000 - call.timeRcvdUnix);
    return formatElapsed(elapsedTime);
  }, [currentTime, call.timeRcvdUnix]);

  // Derived: assigned unit list with elapsed status time string
  const assignedUnits = useMemo(() => {
    if (!call.units) return [] as Array<{ unit: Unit; time: string }>;
    const out: Array<{ unit: Unit; time: string }> = [];
    const nowSec = Math.floor(currentTime / 1000);

    for (const unitId of Object.keys(call.units)) {
      const unit = units.find(u => u.id === unitId);
      if (!unit) continue;
      const statusStart = unit.statusStartedAt || call.timeRcvdUnix || nowSec;
      const timeSince = Math.max(0, nowSec - statusStart);
      out.push({ unit, time: `${formatElapsed(timeSince)} in ${unit.status}` });
    }
    return out;
  }, [call.units, units, currentTime, call.timeRcvdUnix]);

  // Flash changed fields (compare with previous snapshot)
  useEffect(() => {
    const prev = prevCallRef.current;
    if (prev && prev.id === call.id) {
      const changed: string[] = [];
      if (prev.location !== call.location) changed.push('location');
      if (prev.notes !== call.notes) changed.push('notes');
      if (prev.callCode !== call.callCode) changed.push('callCode');
      if (prev.priority !== call.priority) changed.push('priority');
      if (prev.status !== call.status) changed.push('status');
      if (prev.tacChannel !== call.tacChannel) changed.push('tacChannel');
      if (prev.boxArea !== call.boxArea) changed.push('boxArea');
      if (prev.reportingParty !== call.reportingParty) changed.push('reportingParty');
      if (prev.reportedVia !== call.reportedVia) changed.push('reportedVia');

      const prevUnits = Object.keys(prev.units || {}).sort().join(',');
      const currUnits = Object.keys(call.units || {}).sort().join(',');
      if (prevUnits !== currUnits) changed.push('assignedUnits');

      if (changed.length) {
        setFlashingFields(prevSet => {
          const s = new Set(prevSet);
          changed.forEach(f => s.add(f));
          return s;
        });
        const t = setTimeout(() => {
          setFlashingFields(prevSet => {
            const s = new Set(prevSet);
            changed.forEach(f => s.delete(f));
            return s;
          });
        }, 3000);
        return () => clearTimeout(t);
      }
    }
  }, [call]);

  // Update prev snapshot after comparisons
  useEffect(() => {
    prevCallRef.current = {
      id: call.id,
      location: call.location,
      notes: call.notes,
      callCode: call.callCode,
      priority: call.priority,
      status: call.status,
      tacChannel: call.tacChannel,
      boxArea: call.boxArea,
      reportingParty: call.reportingParty,
      reportedVia: call.reportedVia,
      units: call.units
    };
  }, [call]);

  // Prepare comments list (sorted + formatted time + flashing marks)
  const preparedComments: PreparedComment[] = useMemo(() => {
    const list = (call.comments || []).slice().sort((a, b) => (b.number || 0) - (a.number || 0));
    return list.map(c => ({
      id: c.id,
      number: c.number,
      author: c.author,
      text: c.text,
      type: c.type,
      timestamp: c.timestamp,
      formattedTime: new Date(c.timestamp).toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }),
      isFlashing: flashingComments.has(c.id),
      pcrData: c.pcrData
    }));
  }, [call.comments, flashingComments]);

  // Flash only *new* incoming comments and mark them viewed after flashing
  useEffect(() => {
    const comments = call.comments || [];
    if (!comments.length) return;

    const prev = prevCommentIdsRef.current;
    const incomingIds = comments.map(c => c.id);

    // New = present now but not previously seen
    const newIds = incomingIds.filter(id => !prev.has(id));
    if (!newIds.length) return;

    // Start flashing for new IDs
    setFlashingComments(prevSet => {
      const s = new Set(prevSet);
      newIds.forEach(id => s.add(id));
      return s;
    });

    // Update "seen" set immediately so subsequent updates don't re-flash these
    newIds.forEach(id => prev.add(id));

    // Set up timeout for each new comment ID individually
    newIds.forEach(id => {
      // Clear any existing timeout for this ID
      const existingTimeout = timeoutRefsRef.current.get(id);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // Set new timeout for this specific ID
      const timeout = setTimeout(() => {
        setFlashingComments(prevSet => {
          const s = new Set(prevSet);
          s.delete(id);
          return s;
        });
        onCommentsViewed?.([id]);
        timeoutRefsRef.current.delete(id);
      }, 3000);

      timeoutRefsRef.current.set(id, timeout);
    });
  }, [call.comments, onCommentsViewed]);


  // Listen for "open PCR" events from CommentsTable (decouples row click from modal logic)
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ id: string }>;
      const comment = call.comments?.find(c => c.id === ce.detail.id);
      if (!comment) return;
      try {
        let p = null as any;
        if (comment.pcrData) p = typeof comment.pcrData === 'string' ? JSON.parse(comment.pcrData) : comment.pcrData;
        setEditingPCR(p || null);
        setEditingPCRCommentId(comment.id);
        setShowPCRModal(true);
      } catch {
        // ignore parse errors
      }
    };
    window.addEventListener('open-pcr-from-comment', handler as EventListener);
    return () => window.removeEventListener('open-pcr-from-comment', handler as EventListener);
  }, [call.comments]);

  // ---------- Handlers (stable with useCallback) ----------
  const getFlashingStyle = useCallback(
    (fieldName: string) =>
      flashingFields.has(fieldName)
        ? { animation: `${flashBackground} ${jitterDuration(500)} ease-in-out 6`, willChange: 'background-color,color,border-color' }
        : {},
    [flashingFields]
  );

  const handleAttachUnits = useCallback(() => {
    if (!selectedUnits.length) return showWarning('No units selected');

    const unitsToAssign = selectedUnits.filter(id => {
      const u = units.find(x => x.id === id);
      return !(u && u.assignedCall === call.id);
    });
    if (!unitsToAssign.length) return showWarning('All selected units are already assigned to this call');

    const invalidUnits = selectedUnits.filter(id => {
      const u = units.find(x => x.id === id);
      return u && u.status !== 'I/S';
    });
    if (invalidUnits.length) return showWarning(`Units ${invalidUnits.join(', ')} must be In Service (I/S) to be assigned to calls`);

    onAttachUnits(unitsToAssign);
  }, [selectedUnits, units, call.id, onAttachUnits, showWarning]);

  const openStatusMenu = useCallback((anchorEl: HTMLElement, unitId: string) => {
    // Only allow editing own unit if currentUserId constrained
    if (currentUserId && unitId !== currentUserId) {
      showWarning('You can only change the status of your own unit');
      return;
    }
    setSelectedUnitForStatus(unitId);
    setStatusMenuAnchor(anchorEl);
  }, [currentUserId, showWarning]);

  const closeStatusMenu = useCallback(() => {
      setStatusMenuAnchor(null);
      setSelectedUnitForStatus(null);
  }, []);

  const handleStatusSelect = useCallback(
    async (status: string) => {
      if (!selectedUnitForStatus) return;
      closeStatusMenu();
      const unitId = selectedUnitForStatus;

      const clearing = status === 'I/S' || status === 'OOS' || status === 'OOS-GAME';

      if (clearing && currentUserId && !showEditAndCloseButtons) {
        const assignedUnitIds = Object.keys(call.units || {});
        const isLast = assignedUnitIds.length === 1 && assignedUnitIds.includes(unitId);
        if (isLast) {
          setPendingStatusChange({ unitId, status });
          setShowLastUnitPrompt(true);
      return;
        }
    }
    
    try {
        const res = await http.post('/units/status', { unitId, status });
        if (res.status !== 200) console.error('Failed to update unit status', res.status);
      } catch (e) {
        console.error('Failed to update unit status:', e);
      }
    },
    [selectedUnitForStatus, closeStatusMenu, currentUserId, showEditAndCloseButtons, call.units]
  );

  const handleAddComment = useCallback(async () => {
    const text = newComment.trim();
    if (!text) return showWarning('Comment cannot be empty');

    try {
      let author = currentUsername || 'UNKNOWN';
      if (currentUserId) author = `${author} (${currentUserId})`;

      const res = await http.post(`/calls/${call.id}/comments`, { text, author, type: 'GENERAL' });
      if (res.status === 200) {
        showSuccess('Comment added successfully');
        setNewComment('');
      } else {
        showError('Failed to add comment');
      }
    } catch {
      showError('Failed to add comment');
    }
  }, [newComment, currentUsername, currentUserId, call.id, showWarning, showSuccess, showError]);

  const handleEditCall = useCallback(() => setShowEditModal(true), []);
  const handleCallUpdated = useCallback((_updatedCall: Call) => showSuccess(`Call ${call.id} updated successfully`), [showSuccess, call.id]);

  const handleCloseCall = useCallback(async () => {
    setShowCloseConfirmation(false);
    try {
      const res = await http.post(`/calls/${call.id}/close`);
      if (res.status === 200) {
        showSuccess(`Call ${call.id} closed successfully`);
        onCallClosed?.();
      } else {
        showError('Failed to close call');
      }
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Failed to close call';
      showError(msg);
    }
  }, [call.id, onCallClosed, showError, showSuccess]);

  const handleLastUnitCloseCall = useCallback(async () => {
    setShowLastUnitPrompt(false);
    if (!pendingStatusChange) return;
    try {
      await http.post('/units/status', { unitId: pendingStatusChange.unitId, status: pendingStatusChange.status });
      const res = await http.post(`/calls/${call.id}/close`);
      if (res.status === 200) {
        showSuccess(`Unit cleared and call ${call.id} closed`);
        onCallClosed?.();
        }
      setPendingStatusChange(null);
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Failed to close call';
      showError(msg);
      setPendingStatusChange(null);
    }
  }, [pendingStatusChange, call.id, onCallClosed, showError, showSuccess]);

  const handleLastUnitSetPending = useCallback(async () => {
    setShowLastUnitPrompt(false);
    if (!pendingStatusChange) return;
    try {
      await http.post('/units/status', { unitId: pendingStatusChange.unitId, status: pendingStatusChange.status });
      const res = await http.put(`/calls/${call.id}`, { status: 'PENDING' });
      if (res.status === 200) showSuccess(`Unit cleared and call ${call.id} set back to pending`);
      setPendingStatusChange(null);
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Failed to update call status';
      showError(msg);
      setPendingStatusChange(null);
    }
  }, [pendingStatusChange, call.id, showError, showSuccess]);

  // Drag & Drop handlers
  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'copy';
  }, []);
  const onDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(true);
  }, []);
  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false);
  }, []);
  const onDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
        const dataString = e.dataTransfer.getData('text/plain');
        if (!dataString) return;
        
        let unitIds: string[] = [];
        try {
          const parsed = JSON.parse(dataString);
          unitIds = Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      if (!dataString.startsWith('[') && !dataString.startsWith('{')) unitIds = [dataString];
      else return showError('Invalid drag data format');
    }

        unitIds = unitIds.filter(id => id && typeof id === 'string' && id !== 'DISPATCH');
    if (!unitIds.length) return;
        
        const alreadyAssigned: string[] = [];
        const invalidStatus: string[] = [];
        const validUnits: string[] = [];
        
    unitIds.forEach(id => {
      const u = units.find(x => x.id === id);
      if (!u) return;
      if (u.assignedCall === call.id) alreadyAssigned.push(id);
      else if (u.status !== 'I/S') invalidStatus.push(id);
      else validUnits.push(id);
    });

    if (alreadyAssigned.length) showWarning(`Units already assigned to ${call.id}: ${alreadyAssigned.join(', ')}`);
    if (invalidStatus.length) showWarning(`Units must be I/S to assign: ${invalidStatus.join(', ')}`);
    if (!validUnits.length) return;

    try {
      const res = await http.post('/calls/assign', { callId: call.id, unitIds: validUnits });
      if (res.status === 200) showSuccess(`Assigned ${validUnits.length} unit(s) to call ${call.id}`);
      else showError('Failed to assign units to call');
    } catch {
            showError('Failed to assign units to call');
          }
  }, [units, call.id, showError, showSuccess, showWarning]);

  useEffect(() => {
    const onEnd = () => setIsDragOver(false);
    document.addEventListener('dragend', onEnd);
    return () => document.removeEventListener('dragend', onEnd);
  }, []);

  // ---------- Render ----------
  return (
    <Box
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={{ 
        userSelect: 'text',
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        border: isDragOver ? '2px dashed #3c8cff' : 'none',
        backgroundColor: isDragOver ? '#1a1d22' : 'transparent',
        borderRadius: isDragOver ? '8px' : '0px',
        transition: 'height 0.3s ease-in-out'
      }}
    >
      {/* Pending Confirmation Banner (unit UI only) */}
      {call.status === 'PENDING_CONFIRMATION' && currentUserId && !showEditAndCloseButtons && (
        <Dialog
          open
          disableEscapeKeyDown
          PaperProps={{ sx: { backgroundColor: '#1c1f25', border: '1px solid #40444b', borderRadius: '12px', minWidth: 500, maxWidth: 600 } }}
        >
          <DialogTitle sx={{ color: '#eceef1', fontSize: 18, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ color: '#ffa500', fontSize: 24 }} />
            All units cleared - What should happen to this call?
          </DialogTitle>
          <DialogContent sx={{ p: '16px 24px' }}>
            <Typography sx={{ color: '#b4b9c0', fontSize: 14, mb: 2 }}>
              You were the last unit on call {call.id}. Please choose an action:
            </Typography>
            {!allowSelfClose && (
              <Typography sx={{ color: '#ffa500', fontSize: 13, fontStyle: 'italic' }}>
                Note: Dispatcher is active. You can only set the call back to pending.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: '16px 24px', gap: 1 }}>
            <Button
              onClick={async () => {
                try {
                  const res = await http.put(`/calls/${call.id}`, { status: 'PENDING' });
                  if (res.status === 200) showSuccess(`Call ${call.id} set back to pending`);
                } catch (e: any) {
                  const errorMsg = typeof e.response?.data?.error === 'string'
                    ? e.response.data.error
                    : 'Failed to update call';
                  showError(errorMsg);
                }
              }}
              sx={{ backgroundColor: '#2a2a2a', color: '#eceef1', border: '1px solid #464a52', borderRadius: '5px', '&:hover': { backgroundColor: '#3a3a3a' } }}
            >
              Set to Pending
            </Button>
            {allowSelfClose && (
              <Button
                onClick={async () => {
                  try {
                    const res = await http.post(`/calls/${call.id}/close`);
                    if (res.status === 200) {
                      showSuccess(`Call ${call.id} closed`);
                      onCallClosed?.();
                    }
                  } catch (e: any) {
                    const errorMsg = typeof e.response?.data?.error === 'string'
                      ? e.response.data.error
                      : 'Failed to close call';
                    showError(errorMsg);
                  }
                }}
                sx={{ backgroundColor: '#550000', color: '#fff', border: '1px solid #660000', borderRadius: '5px', '&:hover': { backgroundColor: '#440000' } }}
              >
                Close Call
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}

      {/* Main content */}
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, flex: 1, overflow: 'hidden' }}>
        {/* Left (Details) */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
          {/* Header */}
          <Box sx={{ mb: 0.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="h6" sx={{ color: '#fff', fontSize: 16, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box component="span">{call.id}</Box>
            <Box component="span" sx={{ color: '#a0a6b1' }}>•</Box>
            <Box 
              component="span" 
                  sx={flashingFields.has('callCode') ? { animation: `${flashTextColor} ${jitterDuration(500)} ease-in-out 6`, willChange: 'color' } : {}}
            >
              {getCallDescription(call.callCode)}
            </Box>
            <Box component="span" sx={{ color: '#a0a6b1' }}>•</Box>
                <Box component="span" sx={{ color: '#fff' }}>{headerElapsed}</Box>
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {allowSelfDispatch && currentUserId ? (
              <Tooltip title="Self Dispatch (Dispatcher OOS)" placement="bottom">
                <span>
                  <IconButton
                    onClick={() => {
                          if (call.assignedUnits?.includes(currentUserId)) return showWarning('Your unit is already assigned to this call');
                      onAttachUnits([currentUserId]);
                    }}
                        disabled={!!call.assignedUnits?.includes(currentUserId)}
                    sx={{
                      backgroundColor: call.assignedUnits?.includes(currentUserId) ? '#464a52' : '#004204',
                          color: '#fff',
                          width: 32, height: 32, borderRadius: '6px',
                          '&:hover': { backgroundColor: call.assignedUnits?.includes(currentUserId) ? '#464a52' : '#003003' },
                          '&:disabled': { backgroundColor: '#464a52', color: '#8c9198' }
                    }}
                  >
                    <AttachIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
                  <Tooltip title={`Attach ${selectedUnits.length ? `${selectedUnits.length} unit${selectedUnits.length > 1 ? 's' : ''}` : 'units'}`} placement="bottom">
                <span>
                  <IconButton
                    onClick={handleAttachUnits}
                        disabled={!selectedUnits.length}
                    sx={{
                          backgroundColor: selectedUnits.length ? '#004204' : '#464a52',
                          color: '#fff',
                          width: 32, height: 32, borderRadius: '6px',
                          '&:hover': { backgroundColor: selectedUnits.length ? '#003003' : '#464a52' },
                          '&:disabled': { backgroundColor: '#464a52', color: '#8c9198' }
                    }}
                  >
                    <AttachIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            
            {showEditAndCloseButtons && (
              <>
                <Tooltip title="Edit call" placement="bottom">
                      <IconButton onClick={handleEditCall} sx={{ backgroundColor: '#020963', color: '#fff', width: 32, height: 32, borderRadius: '6px', '&:hover': { backgroundColor: '#010542' } }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close call" placement="bottom">
                      <IconButton onClick={() => setShowCloseConfirmation(true)} sx={{ backgroundColor: '#550000', color: '#fff', width: 32, height: 32, borderRadius: '6px', '&:hover': { backgroundColor: '#440000' } }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            
            {allowSelfClose && !showEditAndCloseButtons && (
              <Tooltip title="Close Call (Dispatcher OOS)" placement="bottom">
                    <IconButton onClick={() => setShowCloseConfirmation(true)} sx={{ backgroundColor: '#550000', color: '#fff', width: 32, height: 32, borderRadius: '6px', '&:hover': { backgroundColor: '#440000' } }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

          {/* Details table */}
          <Paper sx={{ backgroundColor: '#1c1f25', border: '1px solid #40444b', borderRadius: '8px', p: 0.5, mb: 0.5, width: '100%' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto 1fr', gap: 0, overflow: 'hidden', border: '1px solid #40444b', borderRadius: '4px' }}>
              {/* BOX */}
              <Box sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderBottom: '1px solid #40444b', borderRight: '1px solid #40444b', minHeight: 28, minWidth: 80 }}>
              <Typography sx={{ color: '#a0a6b1', fontSize: 11, fontWeight: 'bold' }}>BOX:</Typography>
          </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderBottom: '1px solid #40444b', borderRight: '1px solid #40444b', minHeight: 28, minWidth: 0, ...getFlashingStyle('boxArea') }}>
            <Tooltip title={call.boxArea || '?'} placement="top-end">
                  <Typography sx={{ color: '#fff', fontSize: 15, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                {call.boxArea || '?'}
              </Typography>
            </Tooltip>
          </Box>
              {/* TAC */}
              <Box sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderBottom: '1px solid #40444b', borderRight: '1px solid #40444b', minHeight: 28, minWidth: 80 }}>
              <Typography sx={{ color: '#a0a6b1', fontSize: 11, fontWeight: 'bold' }}>TAC:</Typography>
          </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderBottom: '1px solid #40444b', minHeight: 28, minWidth: 0, ...getFlashingStyle('tacChannel') }}>
            <Tooltip title={call.tacChannel || '?'} placement="top-end">
                  <Typography sx={{ color: '#fff', fontSize: 15, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                {call.tacChannel || '?'}
              </Typography>
            </Tooltip>
          </Box>
              {/* LOCATION */}
              <Box sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderBottom: '1px solid #40444b', borderRight: '1px solid #40444b', minHeight: 28, minWidth: 80 }}>
              <Typography sx={{ color: '#a0a6b1', fontSize: 11, fontWeight: 'bold' }}>LOCATION:</Typography>
          </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderBottom: '1px solid #40444b', borderRight: '1px solid #40444b', minHeight: 28, minWidth: 0, ...getFlashingStyle('location') }}>
            <Tooltip title={(call.location || '?').toUpperCase()} placement="top-end">
                  <Typography sx={{ color: '#fff', fontSize: 15, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                {(call.location || '?').toUpperCase()}
              </Typography>
            </Tooltip>
          </Box>
              {/* TIME */}
              <Box sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderBottom: '1px solid #40444b', borderRight: '1px solid #40444b', minHeight: 28, minWidth: 80 }}>
              <Typography sx={{ color: '#a0a6b1', fontSize: 11, fontWeight: 'bold' }}>TIME:</Typography>
          </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderBottom: '1px solid #40444b', minHeight: 28, minWidth: 0 }}>
                <Tooltip title={formatUnixAsMDHMS(call.timeRcvdUnix)} placement="top-end">
                  <Typography sx={{ color: '#fff', fontSize: 15, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                    {formatUnixAsMDHMS(call.timeRcvdUnix)}
              </Typography>
            </Tooltip>
          </Box>
              {/* PRIORITY */}
              <Box sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderRight: '1px solid #40444b', borderBottom: isMaximized ? '1px solid #40444b' : 'none', minHeight: 28, minWidth: 80 }}>
              <Typography sx={{ color: '#a0a6b1', fontSize: 11, fontWeight: 'bold' }}>PRIORITY:</Typography>
          </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderRight: '1px solid #40444b', borderBottom: isMaximized ? '1px solid #40444b' : 'none', minHeight: 28, minWidth: 0, ...getFlashingStyle('priority') }}>
            <Tooltip title={String(call.priority || '?')} placement="top-end">
                  <Typography sx={{ color: '#fff', fontSize: 15, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                {call.priority || '?'}
              </Typography>
            </Tooltip>
          </Box>
              {/* PLAN */}
              <Box sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderBottom: isMaximized ? '1px solid #40444b' : 'none', borderRight: '1px solid #40444b', minHeight: 28, minWidth: 80 }}>
              <Typography sx={{ color: '#a0a6b1', fontSize: 11, fontWeight: 'bold' }}>PLAN:</Typography>
          </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderBottom: isMaximized ? '1px solid #40444b' : 'none', minHeight: 28, minWidth: 0, ...getFlashingStyle('callCode') }}>
            <Tooltip title={getCallBox(call.callCode)} placement="top-end">
                  <Typography sx={{ color: '#fff', fontSize: 15, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                {getCallBox(call.callCode)}
              </Typography>
            </Tooltip>
          </Box>
          
              {/* Extra rows (maximized only) */}
          {isMaximized && (
            <>
                  <Box sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderRight: '1px solid #40444b', minHeight: 28, minWidth: 80 }}>
                  <Typography sx={{ color: '#a0a6b1', fontSize: 11, fontWeight: 'bold' }}>REPORTED BY:</Typography>
              </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderRight: '1px solid #40444b', minHeight: 28, minWidth: 0, ...getFlashingStyle('reportingParty') }}>
                <Tooltip title={(call.reportingParty || 'UNKNOWN').toUpperCase()} placement="top-end">
                      <Typography sx={{ color: call.reportingParty ? '#fff' : '#a0a6b1', fontSize: 15, fontWeight: call.reportingParty ? 'bold' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', fontStyle: call.reportingParty ? 'normal' : 'italic' }}>
                    {(call.reportingParty || 'UNKNOWN').toUpperCase()}
                  </Typography>
                </Tooltip>
              </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderRight: '1px solid #40444b', minHeight: 28, minWidth: 80 }}>
                  <Typography sx={{ color: '#a0a6b1', fontSize: 11, fontWeight: 'bold' }}>CREATED BY:</Typography>
              </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', minHeight: 28, minWidth: 0 }}>
                <Tooltip title={(call.createdBy || '?').toUpperCase()} placement="top-end">
                      <Typography sx={{ color: '#fff', fontSize: 15, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                    {(call.createdBy || '?').toUpperCase()}
                  </Typography>
                </Tooltip>
              </Box>
            </>
          )}
        </Box>
      </Paper>

          {/* Assigned Units */}
          <Box sx={{ mb: 0.25 }}>
            <Typography sx={{ color: '#fff', fontSize: 14, fontWeight: 'bold', letterSpacing: '0.5px' }}>
          ASSIGNED UNITS:
        </Typography>
      </Box>

      <Paper
        sx={{
          backgroundColor: '#1c1f25',
          border: '1px solid #40444b',
          borderRadius: '8px',
              p: 0.5,
              mb: 0.5,
              ...(isMaximized
                ? { flex: 0.8, minHeight: 120, display: 'flex', flexDirection: 'column', overflow: 'hidden' }
                : {}),
              ...(flashingFields.has('assignedUnits')
                ? { animation: `${flashBorder} ${jitterDuration(500)} ease-in-out 6`, willChange: 'border-color' }
                : {})
            }}
          >
        {isMaximized ? (
              <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                {assignedUnits.length ? (
                  <Box
                    sx={{
                display: 'flex', 
                flexWrap: 'wrap',
                gap: '4px',
                overflowY: 'auto',
                      pb: '8px',
                      '&::-webkit-scrollbar': { width: '8px' },
                      '&::-webkit-scrollbar-track': { background: '#1a1d22', borderRadius: '4px' },
                      '&::-webkit-scrollbar-thumb': { background: '#464a52', borderRadius: '4px', '&:hover': { background: '#5a5e66' } },
                scrollbarWidth: 'thin',
                      scrollbarColor: '#464a52 #1a1d22'
                    }}
                  >
                    {assignedUnits.map(({ unit, time }) => (
                      <AssignedUnitChip key={unit.id} unit={unit} time={time} onOpenMenu={openStatusMenu} />
                    ))}
              </Box>
            ) : (
                  <Typography sx={{ color: '#a0a6b1', fontSize: 14 }}>No units assigned</Typography>
            )}
          </Box>
        ) : (
          <Box>
                {assignedUnits.length ? (
                  <Box
                    sx={{
                display: 'flex', 
                flexWrap: 'nowrap', 
                gap: '4px',
                      overflowX: 'auto',
                      overflowY: 'hidden',
                      pt: '8px',
                      pb: '12px',
                      height: 48,
                      alignItems: 'center',
                      '&::-webkit-scrollbar': { height: '8px' },
                      '&::-webkit-scrollbar-track': { background: '#1a1d22', borderRadius: '4px' },
                      '&::-webkit-scrollbar-thumb': { background: '#464a52', borderRadius: '4px', '&:hover': { background: '#5a5e66' } },
                scrollbarWidth: 'thin',
                      scrollbarColor: '#464a52 #1a1d22'
                    }}
                  >
                    {assignedUnits.map(({ unit, time }) => (
                      <AssignedUnitChip key={unit.id} unit={unit} time={time} onOpenMenu={openStatusMenu} />
                    ))}
              </Box>
            ) : (
                  <Typography sx={{ color: '#a0a6b1', fontSize: 14 }}>No units assigned</Typography>
            )}
          </Box>
        )}
      </Paper>

          {/* Notes */}
          <Box sx={{ mb: 0.25 }}>
            <Typography sx={{ color: '#fff', fontSize: 14, fontWeight: 'bold' }}>CALL NOTES:</Typography>
      </Box>
      <Paper
        sx={{
              backgroundColor: '#1c1f25',
              border: '1px solid #40444b',
          borderRadius: '8px',
              p: 0.5,
              mb: 0.5,
          flex: isMaximized ? 0.5 : 1,
              minHeight: 60,
              maxHeight: isMaximized ? 100 : undefined,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
              ...(flashingFields.has('notes') ? { animation: `${flashBorder} ${jitterDuration(500)} ease-in-out 6`, willChange: 'border-color' } : {})
        }}
      >
        <Typography
          sx={{
                color: call.notes ? '#fff' : '#a0a6b1',
            fontSize: 14,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
                flex: 1
          }}
        >
          {call.notes || 'No notes available'}
        </Typography>
      </Paper>
      </Box>

        {/* Right (Comments) */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h6" sx={{ color: '#fff', fontSize: 18, fontWeight: 'bold', textTransform: 'uppercase' }}>
              COMMENTS
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Create PCR" placement="left">
                <IconButton
              onClick={() => setShowPCRModal(true)}
              sx={{
                    width: 32, height: 32, p: 0,
                    backgroundColor: '#3c8cff',
                    color: '#fff',
                    border: '1px solid #3c8cff',
                    borderRadius: '6px',
                    '&:hover': { backgroundColor: '#2a7ae9', borderColor: '#2a7ae9' }
                  }}
                >
                  <Icon icon="mdi:file-document-plus-outline" width="18" height="18" />
                </IconButton>
              </Tooltip>

          {onToggleMaximize && (
                <Tooltip title={isMaximized ? 'Minimize' : 'Maximize'} placement="left">
                  <IconButton
                onClick={onToggleMaximize}
                    sx={{ width: 32, height: 32, p: 0, color: '#a0a6b1', '&:hover': { backgroundColor: '#2a2d32', color: '#fff' } }}
              >
                {isMaximized ? <MinimizeIcon fontSize="small" /> : <MaximizeIcon fontSize="small" />}
                  </IconButton>
            </Tooltip>
          )}
            </Box>
        </Box>
        
        <Paper
          sx={{
            backgroundColor: '#1c1f25',
            border: '1px solid #40444b',
            borderRadius: '10px',
              p: 1.5,
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
            <Box
              sx={{
            flex: 1, 
            overflow: 'auto', 
                mb: 2,
                pr: 1,
                '&::-webkit-scrollbar': { width: '8px' },
                '&::-webkit-scrollbar-track': { background: '#1a1d22', borderRadius: '4px' },
                '&::-webkit-scrollbar-thumb': { background: '#464a52', borderRadius: '4px', '&:hover': { background: '#5a5e66' } },
            scrollbarWidth: 'thin',
                scrollbarColor: '#464a52 #1a1d22'
              }}
            >
              {preparedComments.length ? (
                <CommentsTable
                  comments={preparedComments}
                />
              ) : (
                <Typography sx={{ color: '#a0a6b1', fontSize: 15, textAlign: 'center', mt: 4 }}>
                No comments yet
              </Typography>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              value={newComment}
                onChange={e => setNewComment(e.target.value.toUpperCase())}
                onKeyDown={e => {
                  if (e.key === 'Enter' && newComment.trim()) {
                  e.preventDefault();
                  handleAddComment();
                }
              }}
              placeholder="Add a comment..."
              variant="outlined"
              size="small"
              inputProps={{ style: { textTransform: 'uppercase' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1a1d22',
                    height: 40,
                  '& fieldset': { borderColor: '#464a52' },
                  '&:hover fieldset': { borderColor: '#3c8cff' },
                  '&.Mui-focused fieldset': { borderColor: '#3c8cff' }
                },
                  '& .MuiInputBase-input': { color: '#fff', fontSize: 14, p: '12px 14px' }
              }}
            />
            <Button
              variant="contained"
              onClick={handleAddComment}
              disabled={!newComment.trim()}
                sx={{ backgroundColor: '#004204', color: '#fff', fontSize: 14, minWidth: 80, height: 40, '&:hover': { backgroundColor: '#003003' }, '&:disabled': { backgroundColor: '#464a52' } }}
            >
              ADD
            </Button>
          </Box>
        </Paper>
      </Box>
      </Box>

      {/* Status Picker */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor) && Boolean(selectedUnitForStatus)}
        onClose={closeStatusMenu}
        PaperProps={{ sx: { backgroundColor: '#1c1f25', border: '1px solid #40444b', borderRadius: '6px', minWidth: 120 } }}
      >
        {AVAILABLE_STATUSES.map(status => (
          <MenuItem
            key={status}
            onClick={() => handleStatusSelect(status)}
            sx={{ color: '#eceef1', fontSize: 14, '&:hover': { backgroundColor: '#2a2d32' } }}
          >
            <ListItemText primary={status} />
          </MenuItem>
        ))}
      </Menu>

      {/* Close Confirmation */}
      <Dialog open={showCloseConfirmation} onClose={() => setShowCloseConfirmation(false)} PaperProps={{ sx: { backgroundColor: '#1c1f25', border: '1px solid #40444b' } }}>
        <DialogTitle sx={{ color: '#eceef1' }}>Confirm Close Call</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#b4b9c0' }}>Are you sure you want to close call {call.id}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCloseConfirmation(false)} sx={{ color: '#b4b9c0' }}>
            Cancel
          </Button>
          <Button onClick={handleCloseCall} variant="contained" sx={{ backgroundColor: '#550000', color: '#fff', '&:hover': { backgroundColor: '#440000' } }}>
            Close Call
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Call Modal */}
      {showEditModal && <EditCallModal open={showEditModal} onClose={() => setShowEditModal(false)} call={call} onCallUpdated={handleCallUpdated} />}

      {/* Last Unit Clearing Prompt */}
      <Dialog 
        open={showLastUnitPrompt} 
        onClose={() => {
          setShowLastUnitPrompt(false);
          setPendingStatusChange(null);
        }}
        PaperProps={{ sx: { backgroundColor: '#1c1f25', border: '1px solid #40444b' } }}
      >
        <DialogTitle sx={{ color: '#eceef1' }}>Last Unit Clearing from Call</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#b4b9c0', mb: 2 }}>You are the last unit on call {call.id}.</Typography>
          <Typography sx={{ color: '#b4b9c0' }}>What would you like to do with this call?</Typography>
          {!allowSelfClose && <Typography sx={{ color: '#ffa500', mt: 2, fontSize: '0.9em' }}>Note: Dispatcher is active. You can only set the call back to pending.</Typography>}
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <Button 
            onClick={() => {
              setShowLastUnitPrompt(false);
              setPendingStatusChange(null);
            }}
            sx={{ color: '#b4b9c0' }}
          >
            Cancel
          </Button>
          <Button onClick={handleLastUnitSetPending} variant="contained" sx={{ backgroundColor: '#ffa500', color: '#fff', '&:hover': { backgroundColor: '#ff8c00' } }}>
            Set to Pending
          </Button>
          {allowSelfClose && (
            <Button onClick={handleLastUnitCloseCall} variant="contained" sx={{ backgroundColor: '#550000', color: '#fff', '&:hover': { backgroundColor: '#440000' } }}>
              Close Call
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* PCR Modal */}
      <PCRModal
        open={showPCRModal}
        onClose={() => {
          setShowPCRModal(false);
          setEditingPCR(null);
          setEditingPCRCommentId(null);
        }}
        call={call}
        currentUsername={currentUsername}
        editPCRData={editingPCR || undefined}
        editCommentId={editingPCRCommentId || undefined}
        onPCRSaved={() => {}}
      />
    </Box>
  );
};

export default memo(CallDetails);
